// @ts-nocheck
import type { Record } from '@fulcrumapp/fulcrum-core';
import { Query, FormSchema, SQLQuerySchema } from '@fulcrumapp/query-sql';
import moment from 'moment-timezone';

import removeGpsDeviceCaptureProjection from '../../lib/sql-utils';
import RecordPager from './record-pager';
import SummaryLoader from './summary-loader';
import RecordLinkLoader from './record-link-loader';

export const EXCLUDED_COLUMNS = ['_record_key', '_record_sequence', 'rrule', 'record_series.rrule'];

const DEFAULT_HIDDEN_FORM_COLUMNS = [
  '_record_id',
  '_latitude',
  '_longitude',
  '_version',
  '_server_created_at',
  '_created_at', // Device Created At
  '_updated_at', // Device Updated At
  'created_by.name',
  // '_geometry',
  '_altitude',
  '_speed',
  '_course',
  '_horizontal_accuracy',
  '_vertical_accuracy',
  '_changeset_id',
  // '_title',
  '_created_latitude',
  '_created_longitude',
  '_created_geometry',
  '_created_altitude',
  '_created_horizontal_accuracy',
  '_updated_latitude',
  '_updated_longitude',
  '_updated_geometry',
  '_updated_altitude',
  '_updated_horizontal_accuracy',
  '_edited_duration',
  '_created_duration',
  '_updated_duration',
  ...EXCLUDED_COLUMNS,
];

const DEFAULT_HIDDEN_REPEATABLE_COLUMNS = [
  '_record_id',
  '_latitude',
  '_longitude',
  '_version',
  '_created_at',
  'created_by.name',
  // '_geometry',
  '_changeset_id',
  // '_title',
  '_created_latitude',
  '_created_longitude',
  '_created_geometry',
  '_created_altitude',
  '_created_horizontal_accuracy',
  '_updated_latitude',
  '_updated_longitude',
  '_updated_geometry',
  '_updated_altitude',
  '_updated_horizontal_accuracy',
  '_edited_duration',
  '_created_duration',
  '_updated_duration',
  '_child_record_id',
  '_parent_id',
  '_index',
];

export function columnShouldBeHiddenByDefault(column) {
  if (column && column.element) {
    const isMediaElement = column.element.isPhotoElement
                           || column.element.isVideoElement
                           || column.element.isAudioElement
                           || column.element.isSketchElement;

    if (isMediaElement && (column.part === 'captions' || column.part === 'urls')) {
      return true;
    }

    if (column.element.isAddressElement && column.part != null) {
      return true;
    }

    if (column.element.isSignatureElement && column.part != null) {
      return true;
    }

    if (column.element.isHidden || column.element.hasHiddenParent) {
      return true;
    }
  }

  return false;
}

function injectAfterWhereOpenParen(sql: string, afterOpen: string): string {
  const whereIdx = sql.indexOf('WHERE');
  if (whereIdx === -1) return sql;
  let i = whereIdx + 5;
  while (i < sql.length && (sql[i] === ' ' || sql[i] === '\t' || sql[i] === '\n' || sql[i] === '\r')) i++;
  if (sql[i] !== '(') return sql;
  return `${sql.slice(0, whereIdx)}WHERE (${afterOpen}${sql.slice(i + 1)}`;
}

function addExtraCloseParen(sql: string): string {
  for (let i = 0; i < sql.length; i++) {
    if (sql[i] === ')') {
      let j = i + 1;
      while (j < sql.length && (sql[j] === ' ' || sql[j] === '\t' || sql[j] === '\n' || sql[j] === '\r')) j++;
      if (j >= sql.length || sql.startsWith('ORDER BY', j) || sql.startsWith('LIMIT', j)) {
        return `${sql.slice(0, i + 1)})${sql.slice(i + 1)}`;
      }
    }
  }
  return `${sql})`;
}

function insertBeforeFirstKeyword(sql: string, prefix: string): string {
  const orderByIdx = sql.indexOf('ORDER BY');
  const limitIdx = sql.indexOf('LIMIT');
  const insertIdx = Math.min(
    orderByIdx === -1 ? sql.length : orderByIdx,
    limitIdx === -1 ? sql.length : limitIdx,
  );
  return `${sql.slice(0, insertIdx)}${prefix}${sql.slice(insertIdx)}`;
}

function indexOfWhitespacePreceded(sql: string, keyword: string, limit: number): number {
  let from = 0;
  while (from < limit) {
    const idx = sql.indexOf(keyword, from);
    if (idx === -1 || idx >= limit) return -1;
    if (idx > 0 && (sql[idx - 1] === ' ' || sql[idx - 1] === '\t')) return idx;
    from = idx + keyword.length;
  }
  return -1;
}

function findFirstInnerKeyword(
  sql: string,
  limit: number,
): { wsStart: number; keywordStart: number; keyword: string } | null {
  let best: { wsStart: number; keywordStart: number; keyword: string } | null = null;
  for (const keyword of ['ORDER BY', 'LIMIT'] as const) {
    const idx = indexOfWhitespacePreceded(sql, keyword, limit);
    if (idx !== -1) {
      let wsStart = idx - 1;
      while (wsStart > 0 && (sql[wsStart - 1] === ' ' || sql[wsStart - 1] === '\t')) wsStart--;
      if (best === null || wsStart < best.wsStart) {
        best = { wsStart, keywordStart: idx, keyword };
      }
    }
  }
  return best;
}

function replaceFirstInnerKeyword(sql: string, prefix: string): string {
  const found = findFirstInnerKeyword(sql, sql.length);
  if (found === null) return sql;
  return `${sql.slice(0, found.wsStart)}${prefix}${sql.slice(found.keywordStart)}`;
}

const POLYGON_WKT_REGEX = /^(POLYGON|MULTIPOLYGON)\s*\([\d\s.,()-]+\)$/i;

function isValidPolygonWKT(wkt: unknown): wkt is string {
  return typeof wkt === 'string' && POLYGON_WKT_REGEX.test(wkt.trim());
}

function escapeWKTForSQL(wkt: string): string {
  return wkt.replaceAll("'", "''");
}

function getPolygonFilter(polygonWKT: unknown): string | null {
  if (!isValidPolygonWKT(polygonWKT)) {
    return null;
  }

  return `ST_Within("_geometry", ST_GeomFromText('${escapeWKTForSQL(polygonWKT)}', 4326))`;
}

export default class DataViewSource {
  constructor(dataSource, form, repeatableKey, rawColumns, view, loadHandler, queryOptions, { changesetID }) {
    this._mode = 'form';

    this._form = form;
    this._repeatableKey = repeatableKey;
    this._rawColumns = rawColumns;

    this._formSchema = new FormSchema(form, rawColumns.form, rawColumns.repeatables, { fullSchema: true });

    if (this._repeatableKey && this._formSchema.repeatableSchemasByKey[this._repeatableKey]) {
      this._schema = this._formSchema.repeatableSchemasByKey[this._repeatableKey];
    } else {
      this._schema = this._formSchema;
    }

    queryOptions = queryOptions || {};

    let viewOptions = view && view.queries[0];
    if (!viewOptions) {
      viewOptions = {
        columns: this._schema.columns.map((column) => {
          const optionsColumn = queryOptions.columns ? queryOptions.columns.find((c) => c.column.id === column.id) : null;
          const hidden = optionsColumn ? optionsColumn.hidden
            : this.isColumnHiddenByDefault(column) || columnShouldBeHiddenByDefault(column);

          return { column: { id: column.id }, hidden: !!hidden };
        }),
      };
    }

    let changesetFilter = null;

    if (changesetID) {
      changesetFilter = {
        changeset_filter: {
          field: '_changeset_id', value: [changesetID],
        },
      };
    }

    let timeZone = moment.tz.guess();

    try {
      timeZone = Fulcrum.globals.user.time_zone;
    } catch (e) {
      // just fall back to the browser's timezone if it's not available on the user
    }

    queryOptions = {
      ...queryOptions,
      form: this.form,
      repeatableKey,
      schema: this._schema,
      ...viewOptions,
      ...changesetFilter,
      options: {
        time_zone: timeZone,
      },
    };

    this._query = new Query(queryOptions);

    if (viewOptions?.polygonWKT) {
      if (isValidPolygonWKT(viewOptions.polygonWKT)) {
        this._query.polygonWKT = viewOptions.polygonWKT;
      } else {
        console.warn('DataViewSource: ignoring invalid polygonWKT from saved view');
      }
    }

    this._pager = new RecordPager(form, this.repeatableKey, dataSource, this);
    this._summaryLoader = new SummaryLoader(this);
    this._recordLinkLoader = new RecordLinkLoader(this);

    this._isLoading = false;
    this._isAboutToLoad = false;
    this._loadHandler = loadHandler;
    this._dataSource = dataSource;

    this.invalidateSchema();
    this.invalidate();
  }

  get isSQL() {
    return this._mode === 'sql';
  }

  get sql() {
    return this._sql;
  }

  get isMappable() {
    if (this._mode === 'form') {
      return this.form.isGeometryEnabled;
    }

    return this.schema.geometryColumns.length > 0;
  }

  get dataSource() {
    return this._dataSource;
  }

  get summaryLoader() {
    return this._summaryLoader;
  }

  isColumnHiddenByDefault(column) {
    if (this.repeatableKey) {
      return DEFAULT_HIDDEN_REPEATABLE_COLUMNS.indexOf(column.id) > -1;
    }

    return DEFAULT_HIDDEN_FORM_COLUMNS.indexOf(column.id) > -1;
  }

  invalidate(record?: Record) {
    this._lastUpdated = new Date();

    if (this.pager) {
      // don't reset() here because it clears the pages and causes a flicker
      this.pager.invalidate();
    }

    if (this._summaryLoader) {
      this._summaryLoader.invalidate();
    }

    if (record) {
      delete this.dataSource.sources[0].cache[record.id];
    }
  }

  invalidateRecordLinks(ids) {
    if (this._recordLinkLoader) {
      this._recordLinkLoader.invalidate(ids);
    }
  }

  invalidateSchema() {
    this._lastUpdatedSchema = new Date();
  }

  get needsReload() {
    return this._lastFetched !== this.lastUpdated;
  }

  get isLoading() {
    return this._isLoading;
  }

  get isAboutToLoad() {
    return this._isAboutToLoad;
  }

  reloadIfNecessaryInternal() {
    const { lastUpdated } = this;

    if (this._lastFetched !== lastUpdated) {
      this._lastFetched = lastUpdated;
      this._isLoading = true;
      this._isAboutToLoad = false;
      this.pager.reset();

      // invoke updater so the graph gets a chance to react to the changing of the load state
      // this lets components to be notified of the start of reloading of the data
      this._loadHandler();

      this.getRow(this.pager.minRecordOffset, lastUpdated);
    }
  }

  reloadIfNecessary() {
    const LOAD_DELAY = 100;

    this._isAboutToLoad = true;

    clearTimeout(this._reloadTimer);

    this._reloadTimer = setTimeout(() => {
      this._reloadTimer = null;
      this.reloadIfNecessaryInternal();
    }, LOAD_DELAY);
  }

  getRow(index, lastUpdated, delay) {
    // eslint-disable-next-line no-param-reassign
    lastUpdated = lastUpdated || this.lastUpdated;

    const handler = () => {
      this._lastFetched = lastUpdated;
      this._isLoading = false;
      this._isAboutToLoad = false;
    };

    return this.pager.getRow(index, (err) => {
      if (err && err.message !== 'stale response') {
        console.error(err);
      }

      if (handler) {
        handler();
      }
      this._loadHandler();
    }, delay);
  }

  recordAtIndex(index) {
    return this.pager.rowAtIndex(index, { allowStale: true });
  }

  clearAllFilters() {
    this.query.clearAllFilters();
  }

  resetDefaultColumns() {
    this.query.columnSettings.reset();

    for (const setting of this.query.columnSettings.columns) {
      const hidden = this.isColumnHiddenByDefault(setting.column)
                     || columnShouldBeHiddenByDefault(setting.column);

      setting.hidden = hidden;
    }
  }

  get pager() {
    return this._pager;
  }

  get lastUpdated() {
    return this._lastUpdated && this._lastUpdated.getTime();
  }

  get lastUpdatedSchema() {
    return this._lastUpdatedSchema && this._lastUpdatedSchema.getTime();
  }

  get form() {
    return this._form;
  }

  get repeatableKey() {
    return this._repeatableKey;
  }

  get query() {
    return this._query;
  }

  get schema() {
    return this._schema;
  }

  get boundingBox() {
    return this.query.boundingBox;
  }

  set boundingBox(box) {
    if (JSON.stringify(box) !== JSON.stringify(this.query.boundingBox)) {
      this.query.boundingBox = box;
      this.invalidate();
    }
  }

  get polygonWKT() {
    return this.query.polygonWKT;
  }

  set polygonWKT(wkt) {
    if (wkt != null && !isValidPolygonWKT(wkt)) {
      console.warn('DataViewSource: ignoring invalid polygonWKT value');
      return;
    }
    if (wkt !== this.query.polygonWKT) {
      this.query.polygonWKT = wkt;
      this.invalidate();
    }
  }

  get searchFilter() {
    return this.query.searchFilter;
  }

  set searchFilter(filter) {
    if (filter !== this.query.searchFilter) {
      this.query.searchFilter = filter;
      this.invalidate();
    }
  }

  toDistinctValuesSQL(options) {
    return this.query.toDistinctValuesSQL(options);
  }

  toHistogramSQL(options) {
    return this.query.toHistogramSQL(options);
  }

  toCountSQL() {
    let sql = this.query.toCountSQL();

    const polygonFilter = getPolygonFilter(this.query.polygonWKT);

    if (polygonFilter) {
      if (sql.includes('WHERE')) {
        sql = injectAfterWhereOpenParen(sql, `${polygonFilter} AND (`);
        sql = addExtraCloseParen(sql);
      } else {
        sql = insertBeforeFirstKeyword(sql, `WHERE ${polygonFilter} `);
      }
    }

    const hasGpsDeviceCaptureColumn = !!this._rawColumns?.form?.some(
      (column) => column?.name === '_gps_device_capture',
    );

    if (!hasGpsDeviceCaptureColumn) {
      sql = removeGpsDeviceCaptureProjection(sql);
    }

    return sql;
  }

  toSQL(options) {
    let sql = this.query.toSQL(options);

    const polygonFilter = getPolygonFilter(this.query.polygonWKT);

    if (polygonFilter) {
      const subqueryBoundary = sql.indexOf(') AS "records"');
      const innerClause = findFirstInnerKeyword(sql, subqueryBoundary);

      if (sql.includes('WHERE')) {
        sql = injectAfterWhereOpenParen(sql, `${polygonFilter} AND (`);
        if (innerClause) {
          sql = replaceFirstInnerKeyword(sql, ') ');
        } else {
          sql = sql.replace(') AS "records"', ')) AS "records"');
        }
      } else if (innerClause) {
        sql = replaceFirstInnerKeyword(sql, ` WHERE ${polygonFilter} `);
      } else {
        sql = sql.replace(') AS "records"', ` WHERE ${polygonFilter}) AS "records"`);
      }
    }

    const hasGpsDeviceCaptureColumn = !!this._rawColumns?.form?.some(
      (column) => column?.name === '_gps_device_capture',
    );

    if (!hasGpsDeviceCaptureColumn) {
      sql = removeGpsDeviceCaptureProjection(sql);
    }

    return sql;
  }

  toTileSQL(maxTileRecords) {
    // Intentionally does NOT inject polygonWKT — the lasso polygon filters the
    // data table and record count only. Map tiles always show all records so
    // that features outside the selection remain visible on the map.
    return this.query.toTileSQL(maxTileRecords);
  }

  fetchHistogram(options, callback) {
    const params = { q: this.toHistogramSQL(options) };

    this._dataSource.source.query(params, callback);
  }

  queryAST(sql, callback) {
    this.dataSource.source.queryAST({ q: sql }, (err, ast) => {
      if (err || ast.error || ast.query.length === 0) {
        return callback(err, ast);
      }

      let queryAST = ast.query[0];

      // if it's a UNION query (no targetList) wrap it in a subquery
      if (!queryAST.SelectStmt.targetList) {
        queryAST = this.query.toSchemaAST(queryAST, {});
      }

      // get the AST for the wrapped WHERE 0 = 1 no-result query used to pull the real column definition
      const metaAST = this.query.toSchemaAST(queryAST, { schemaOnly: true });

      const metaSQL = this.query.deparse(metaAST);

      this.dataSource.source.query({ q: metaSQL, metadata: true }, (err, rows, res) => {
        const errorMessage = err && err.body ? JSON.parse(err.body).error : null;
        return callback(null, {
          error: errorMessage, ast, queryAST, fields: res && res.fields, columns: res && res.columns,
        });
      });

      return null;
    });
  }

  loadSQL(sql, callback) {
    this.queryAST(sql, (err, {
      error, ast, queryAST, fields, columns,
    } = {}) => {
      if (err) {
        return callback(err);
      }

      this.setSQLFromAST({
        sql, queryAST, fields, columns,
      });

      return callback();
    });
  }

  setSQLFromAST({
    sql, queryAST, columns, fields,
  }) {
    this._sql = sql;

    const rawColumns = [];

    const SQL_TYPE_MAP = {
      date: 'timestamp',
      float: 'double',
    };

    for (let index = 0; index < columns.length; ++index) {
      const type = /\[\]$/.test(fields[index].type) ? 'array' : fields[index].type;

      const dataViewType = SQL_TYPE_MAP[type] || type;

      rawColumns.push({
        field: columns[index].field,
        name: columns[index].name,
        ordinal: index,
        table_field: null,
        type: dataViewType,
      });
    }

    this.setSQL(sql, queryAST, rawColumns);
  }

  setSQL(sql, ast, rawColumns) {
    this._mode = 'sql';
    this._ast = ast;

    this._form = null;
    this._rawColumns = rawColumns;
    this._schema = new SQLQuerySchema(rawColumns);

    const queryOptions = {
      form: null,
      schema: this._schema,
      ast,
      options: {
        time_zone: moment.tz.guess(),
      },
    };

    this._query = new Query(queryOptions);
    this._pager = new RecordPager(null, null, this.dataSource, this);
    this._summaryLoader = new SummaryLoader(this);
    this._recordLinkLoader = new RecordLinkLoader(this);
    this._isLoading = false;
    this._isAboutToLoad = false;

    this.invalidateSchema();
    this.invalidate();

    this.reloadIfNecessary();
  }

  fetchRecordLink(recordLinkField, handler) {
    this._recordLinkLoader.fetch(recordLinkField, handler);
  }

  getRecordLinkTitles(ids) {
    return this._recordLinkLoader.getRecordLinkTitles(ids);
  }

  setRecordLinkTitle(id, title) {
    this._recordLinkLoader.setRecordLinkTitle(id, title);
  }

  reloadSummary(column) {
    const loader = this.summaryLoader.loader(column);

    loader.reset();

    const value = this.getSummaryValue(column);

    // Call the load handler now. This handles the case where the aggregate is set to None (cleared)
    this._loadHandler();

    return value;
  }

  getSummaryValue(column) {
    const columnSetting = this.query.columnSettings.byColumn(column);

    if (!columnSetting.summary.aggregate) {
      return null;
    }

    const loader = this.summaryLoader.loader(columnSetting.column);

    const value = loader.getValue();

    return value;
  }

  eachRow(handler, done, error) {
    done = done || (() => {
      // nothing
    });

    error = error || done;

    this._eachRow(handler).then(done).catch(error);
  }

  async _eachRow(handler) {
    const getNextRow = (index) => new Promise((resolve, reject) => {
      const result = this.pager.getRow(index, (err, pager) => {
        if (err) {
          return reject(err);
        }

        return resolve(pager.rowAtIndex(index, { allowStale: false }));
      }, 0);

      if (result != null) {
        return resolve(result);
      }

      return null;
    });

    for (let i = 0; i < this.pager.totalCount; ++i) {
      const row = await getNextRow(i);

      await handler(row, i);
    }

    return null;
  }
}
